import React from "react";
import ExclamationIcon from "../../assets/exclamation.svg";
import {
  COLUMN_WIDTHS,
  aggridHelpers,
} from "../../helpers/common/ag-grid-helper";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";

export const createZoneViewColumns = (t) => {
  const baseColumnConfig = {
    pivot: true,
    floatingFilter: true,
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
    },
    pivotComparator: (a, b) => b.localeCompare(a),
  };

  function resolveNested(obj, path) {
    return path.split(".").reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  const valueRender = (params) => {
    const text = String(params.value);
    if (text?.includes("-color")) {
      return (
        <div>
          <p className="red-note-text">
            <span className="red-note">
              {Number(text?.replace("-color", ""))}
            </span>
          </p>
        </div>
      );
    }
    return isNaN(text) ? "" : Number(text);
  };

  const unitsCommonFilter = {
    pivot: true,
    floatingFilter: true,
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filters: [
        {
          filter: "agNumberColumnFilter",
          filterParams: {
            valueFormatter: (params) => {
              return Number(String(params.value)?.replace("-color", ""));
            },
            maxNumConditions: 1,
          },
        },
      ],
    },
    valueGetter: (params) => {
      const value = resolveNested(params.data, params.colDef.field);
      return Number(String(value)?.replace("-color", ""));
    },
    processCellForExport: (params) => {
      return String(params.value)?.replace("-color", "");
    },
    pivotComparator: (a, b) => b.localeCompare(a),
    cellRenderer: (params) => {
      const value = resolveNested(params.data, params.colDef.field);
      return valueRender({
        value: value,
      });
    },
  };

  const createBasicColumn = (
    field,
    headerKey,
    width = COLUMN_WIDTHS.medium
  ) => ({
    ...baseColumnConfig,
    field,
    headerName: t(headerKey),
    key: field,
    width,
  });

  return [
    createBasicColumn("sl_no", "slogColumntitle.sl.no", COLUMN_WIDTHS.small),
    {
      ...createBasicColumn(
        "status",
        "slogColumntitle.status",
        COLUMN_WIDTHS.small
      ),
      cellRenderer: (params) =>
        params.value
          ? logDataHelperFunctions.getIconForStatus(params.value)
          : "",
      processCellForExport: (params) =>
        params.value
          ? logDataHelperFunctions.getExportValueForStatus(
              params.value?.toLowerCase()
            )
          : "",
      filterParams: {
        filters: [
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellHeight: 40,
              cellRenderer: (params) => {
                const status = String(params.value);
                if (status === "(Select All)") {
                  return "(Select All)";
                }
                return logDataHelperFunctions.getIconForStatus(status);
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
            },
          },
        ],
        maxNumConditions: 1,
      },
    },
    createBasicColumn("stationNumbers", "slogColumntitle.stationName"),
    createBasicColumn("buyType", "slogColumntitle.buyType"),
    createBasicColumn("type", "slogColumntitle.torch"),
    {
      ...createBasicColumn("pass", "slogColumntitle.pass"),
      cellRenderer: (params) =>
        params.value === "!" ? (
          <img src={ExclamationIcon} alt="Exclamation" />
        ) : (
          params.value
        ),
    },
    {
      ...createBasicColumn("zone", "slogColumntitle.zone"),
      cellRenderer: (params) =>
        params.value === "!" ? (
          <img src={ExclamationIcon} alt="Exclamation" />
        ) : (
          params.value
        ),
    },
    {
      ...createBasicColumn("weldStartTimeRaw", "slogColumntitle.weldstartTime"),
      cellRenderer: (params) =>
        Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(params.value, true)
          : "-",
      processCellForExport: (params) =>
        Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(params.value, true)
          : "-",
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue) =>
                aggridHelpers.dateFilter(filterLocalDateAtMidnight, cellValue),
              maxNumConditions: 1,
              inRangeInclusive: true,
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: (params) => {
                const value = String(params.value);
                if (value === "(Select All)") {
                  return "(Select All)";
                }
                return Number(params.value) > 0
                  ? logDataHelperFunctions.formatWeldStartTimeLive(
                      params.value,
                      true
                    )
                  : "-";
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
              comparator: (filterLocalDateAtMidnight, cellValue) =>
                aggridHelpers.dateFilter(filterLocalDateAtMidnight, cellValue),
            },
          },
        ],
      },
    },
    {
      ...createBasicColumn(
        "weld_time",
        "slogColumntitle.weldTime",
        COLUMN_WIDTHS.large
      ),
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agNumberColumnFilter",
            filterParams: { maxNumConditions: 1 },
          },
        ],
      },
      valueGetter: (params) => {
        const value = resolveNested(params.data, params.colDef.field);
        return isNaN(value) ? value : Number(value);
      },
    },
    createBasicColumn("autoIncrement", "slogColumntitle.autoIncrement"),
    {
      headerName: t("slogColumntitle.current"),
      key: "currentA",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "current_low",
          key: "currentLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "current_avg",
          key: "currentAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "current_high",
          key: "currentHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.volt"),
      key: "voltV",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "volt_low",
          key: "voltLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "volt_avg",
          key: "voltAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "volt_high",
          key: "voltHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.travelSpeed"),
      key: "travelSpeed",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "travel_speed_low",
          key: "travelLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "travel_speed_avg",
          key: "travelAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "travel_speed_high",
          key: "travelHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.trueEnergy"),
      key: "trueEnergy",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "true_energy_low",
          key: "trueEnergyLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "true_energy_avg",
          key: "trueEnergyAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "true_energy_high",
          key: "trueEnergyHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.heat"),
      key: "heat",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "heat_low",
          key: "heatLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "heat_avg",
          key: "heatAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "heat_high",
          key: "heatHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.wireSpeed"),
      key: "wireSpeed",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "wire_speed_low",
          key: "wireSpeedLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "wire_speed_avg",
          key: "wireSpeedAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "wire_speed_high",
          key: "wireSpeedHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.oscillationWidth"),
      key: "oscillationWidth",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "oscillation_width_low",
          key: "oscillationWidthLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "oscillation_width_avg",
          key: "oscillationWidthAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "oscillation_width_high",
          key: "oscillationWidthHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.frequency"),
      key: "frequency",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "frequency_low",
          key: "frequencyLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "frequency_avg",
          key: "frequencyAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "frequency_high",
          key: "frequencyHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.target"),
      key: "target",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "target_low",
          key: "targetLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "target_avg",
          key: "targetAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "target_high",
          key: "targetHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.horizontalBias"),
      key: "horizontalBias",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "horizontal_bias_low",
          key: "horizontalBiasLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "horizontal_bias_avg",
          key: "horizontalBiasAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "horizontal_bias_high",
          key: "horizontalBiasHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.totalWireConsumed"),
      key: "totalWireConsumed",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "total_wire_consumed_low",
          key: "totalWireConsumedLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "total_wire_consumed_avg",
          key: "totalWireConsumedAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "total_wire_consumed_high",
          key: "totalWireConsumedHigh",
          ...unitsCommonFilter,
        },
      ],
    },
    {
      headerName: t("slogColumntitle.arcImpedance"),
      key: "arcImpedance",
      children: [
        {
          headerName: t("slogColumntitle.low"),
          field: "arc_impedance_low",
          key: "arcImpedanceLow",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.avg"),
          field: "arc_impedance_avg",
          key: "arcImpedanceAvg",
          ...unitsCommonFilter,
        },
        {
          headerName: t("slogColumntitle.high"),
          field: "arc_impedance_high",
          key: "arcImpedanceHigh",
          ...unitsCommonFilter,
        },
      ],
    },
  ];
};
